// VerticalTimeline.js — the MultiRecord++ V0.5 day. A true-to-clock day view
// (Google-Calendar style): one day at a time on a fixed time grid, midnight to
// midnight, with the empty hours shown. Visits sit at their real clock
// position (anchored on createdAt) and their height reflects how long we think
// the visit runs (stretched to the next visit, capped). Concurrent visits go
// side-by-side. The red "now" line sits at the actual current time and only
// shows on today. A ◀ ▶ day stepper above the grid switches today/yesterday;
// the scroll is just within the chosen day.
//
// Design: docs/comprehend-live-v05.md.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useComprehendLive } from 'agent/useComprehendLive';
import { computeActiveSet } from 'agent/multiRecord';
import QuickAddVisit from './QuickAddVisit';
import { T } from './liveTheme';

// Vertical scale is zoomable (px per minute). Real visits sit minutes/hours
// apart so a loose scale reads fine; the demo packs visits ~2 min apart, so it
// defaults tight (zoomed in) or they pile into one column.
const ZOOM_DEFAULT = 1.5; // ~30-min gridline ticks by default (30*1.5=45 >= MIN_TICK_PX)
const ZOOM_DEMO = 24; // demo visits are ~2 min apart — zoom in so they read clean
const ZOOM_MIN = 0.4;
const ZOOM_MAX = 40; // allow zooming in tight
const ZOOM_STEP = 1.4; // multiplicative per click
const ZOOM_KEY = 'comprehendpt_live_calZoom'; // persisted px-per-min

const readSavedZoom = () => {
  try {
    const v = parseFloat(localStorage.getItem(ZOOM_KEY));
    return Number.isFinite(v) && v > 0 ? v : null;
  } catch (e) {
    return null;
  }
};
const MIN_BLOCK_H = 18; // small floor for tap target (don't inflate into next)
const MIN_DUR_MIN = 2; // we don't know real length: assume 2 min min...
const MAX_DUR_MIN = 60; // ...up to 60 min, otherwise duration = time to next visit
const SUBTITLE_MIN_H = 34; // show the patient·time line once it fits without clipping
const GUTTER = 58; // room for a full time-of-day label (e.g. "12:00p")
const MINUTE_MS = 60000;
const DAY_MS = 24 * 60 * 60 * 1000;
// Gridline interval options (minutes); pick the densest that still leaves
// ~MIN_TICK_PX between lines at the current zoom.
const TICK_OPTIONS = [1, 2, 3, 5, 10, 15, 30, 60];
const MIN_TICK_PX = 38;

const COLUMN_ACCENTS = [T.greenBright, T.tealSoft, T.amber, T.redSoft];

export const snapToMinute = (ms) =>
  Math.round(ms / MINUTE_MS) * MINUTE_MS;
export const snapToGrid = (ms, intervalMin) =>
  Math.round(ms / (intervalMin * MINUTE_MS)) * intervalMin * MINUTE_MS;

// Compact time ("10:30a", "12:00p") so the gutter label never wraps.
const fmtTime = (ms) => {
  const d = new Date(ms);
  let h = d.getHours();
  const m = d.getMinutes();
  const ap = h < 12 ? 'a' : 'p';
  h %= 12;
  if (h === 0) h = 12;
  return `${h}:${String(m).padStart(2, '0')}${ap}`;
};

const midnightOffset = (offset) => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + offset);
  return d.getTime();
};

const dayLabel = (offset) => {
  if (offset === 0) return 'Today';
  if (offset === -1) return 'Yesterday';
  if (offset === 1) return 'Tomorrow';
  return new Date(midnightOffset(offset)).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
};

// Assign overlapping events to side-by-side columns. Overlap is judged on the
// VISUAL extent (top..top+height) so a short visit's min-height block still
// shares a column with whatever it visually covers.
export const layoutDay = (events, pxPerMin) => {
  const withGeom = events.map((e) => {
    const rawMin = (e.endMs - e.startMs) / 60000;
    const durMin = Math.max(MIN_DUR_MIN, Math.min(MAX_DUR_MIN, rawMin));
    const height = Math.max(MIN_BLOCK_H, durMin * pxPerMin);
    const top = ((e.startMs - e.dayStartMs) / 60000) * pxPerMin;
    return { ...e, top, height };
  });
  // Cluster/column by rendered position so visits that would visually overlap
  // go side-by-side. Because top is derived from pxPerMin, zooming out widens
  // the time window that counts as visually close.
  const sorted = [...withGeom].sort((a, b) => a.top - b.top);
  const clusters = [];
  let cur = [];
  let curEnd = -Infinity;
  for (const e of sorted) {
    if (cur.length && e.top >= curEnd) {
      clusters.push(cur);
      cur = [];
      curEnd = -Infinity;
    }
    cur.push(e);
    curEnd = Math.max(curEnd, e.top + e.height);
  }
  if (cur.length) clusters.push(cur);

  const out = [];
  for (const cluster of clusters) {
    const colEnds = [];
    for (const e of cluster) {
      let col = colEnds.findIndex((end) => end <= e.top);
      if (col === -1) {
        col = colEnds.length;
        colEnds.push(e.top + e.height);
      } else {
        colEnds[col] = e.top + e.height;
      }
      e._col = col;
    }
    const cols = colEnds.length;
    cluster.forEach((e) => out.push({ ...e, col: e._col, cols }));
  }
  return out;
};

const ContextMenu = ({ x, y, onView, onRename, onPatient, onClose }) => (
  <div
    onClick={(e) => e.stopPropagation()}
    style={{
      position: 'fixed',
      top: y,
      left: x,
      background: T.surfaceCard,
      border: `1px solid ${T.border}`,
      borderRadius: 8,
      boxShadow: '0 6px 20px rgba(0,0,0,0.35)',
      zIndex: 10000003,
      minWidth: 160,
      overflow: 'hidden',
    }}
  >
    {[
      { label: 'View visit', icon: 'fa-external-link-alt', fn: onView },
      { label: 'Rename visit', icon: 'fa-pencil', fn: onRename },
      { label: 'Edit patient name', icon: 'fa-user', fn: onPatient },
    ].map((item) => (
      <div
        key={item.label}
        role="button"
        onClick={() => {
          item.fn();
          onClose();
        }}
        style={{
          padding: '8px 12px',
          fontSize: 12,
          color: T.textSoft,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
        onMouseEnter={(e) =>
          (e.currentTarget.style.background = T.surfaceMuted)
        }
        onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
      >
        <i
          className={`fa ${item.icon}`}
          aria-hidden="true"
          style={{ fontSize: 11, color: T.textFaint, width: 12 }}
        />
        {item.label}
      </div>
    ))}
  </div>
);

const VerticalTimeline = () => {
  const { state, actions } = useComprehendLive();
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [dayOffset, setDayOffset] = useState(0); // 0 = today, -1 = yesterday
  const [menu, setMenu] = useState(null);
  const [addWin, setAddWin] = useState(null); // {startMs, endMs} for a new visit
  const [pxPerMin, setPxPerMin] = useState(
    () => readSavedZoom() ?? ZOOM_DEFAULT,
  );
  // If the user has a saved zoom, respect it (don't let the demo default stomp).
  const userZoomedRef = useRef(readSavedZoom() != null);
  const scrollTargetRef = useRef(null);
  const gridRef = useRef(null);
  const [dragId, setDragId] = useState(null); // conversationId being dragged
  const [dragDelta, setDragDelta] = useState(0); // px moved from the grab point
  const dragRef = useRef(null);
  const suppressCardClickRef = useRef(false);
  const [createDrag, setCreateDrag] = useState(null); // {startMs, curMs} ghost

  useEffect(() => {
    const t = setInterval(() => setNowMs(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);

  // Demo packs visits close together — default to a tight zoom so they don't
  // collapse into one column. Stops applying once the user zooms manually.
  useEffect(() => {
    if (!userZoomedRef.current)
      setPxPerMin(state.demo ? ZOOM_DEMO : ZOOM_DEFAULT);
  }, [state.demo]);

  const zoom = (factor) => {
    userZoomedRef.current = true;
    setPxPerMin((p) => {
      const next = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, p * factor));
      try {
        localStorage.setItem(ZOOM_KEY, String(next));
      } catch (e) {
        // storage unavailable
      }
      return next;
    });
  };

  useEffect(() => {
    const close = () => setMenu(null);
    if (menu) {
      window.addEventListener('click', close);
      return () => window.removeEventListener('click', close);
    }
    return undefined;
  }, [menu]);

  const dayStartMs = midnightOffset(dayOffset);
  const dayEndMs = dayStartMs + DAY_MS;
  const isToday = dayOffset === 0;

  const sessionFor = (id) =>
    state.sessions.find((s) => String(s.conversationId) === String(id)) || null;

  const activeSet = new Set(
    computeActiveSet(state.schedule, nowMs, state.manualOverrides, {
      beforeMs: (state.bufferBeforeMin || 0) * 60000,
      afterMs: (state.bufferAfterMin || 0) * 60000,
      auto: state.autoSwitch,
    }).map(String),
  );

  // Events for the selected day only.
  const laid = useMemo(() => {
    const events = [];
    for (const slot of state.schedule || []) {
      const startMs = new Date(slot.start).getTime();
      const endMs = new Date(slot.end).getTime();
      if (
        Number.isNaN(startMs) ||
        startMs < dayStartMs ||
        startMs >= dayEndMs
      ) {
        continue;
      }
      for (const p of slot.patients || []) {
        const patientEndMs =
          p.end == null ? NaN : new Date(p.end).getTime();
        events.push({
          conversationId: String(p.conversationId),
          patientName: p.patientName,
          startMs,
          endMs: !Number.isNaN(patientEndMs)
            ? patientEndMs
            : Number.isNaN(endMs)
              ? startMs + 30 * 60000
              : endMs,
          dayStartMs,
        });
      }
    }
    return layoutDay(events, pxPerMin);
  }, [state.schedule, dayStartMs, dayEndMs, pxPerMin]);

  const dayHeight = 24 * 60 * pxPerMin;

  // Auto-scroll the chosen day into view: to "now" on today, else the first
  // visit. Runs on day change, zoom change, or first paint.
  useEffect(() => {
    const el = scrollTargetRef.current;
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ block: 'center' });
    }
  }, [dayOffset, laid.length, pxPerMin]);

  const nowTop = ((nowMs - dayStartMs) / 60000) * pxPerMin;
  const firstTop = laid.length ? Math.min(...laid.map((e) => e.top)) : 0;
  // The element auto-scroll centers on.
  const scrollTargetTop = isToday ? nowTop : firstTop;

  // Keep grid density zoom-dependent, but always snap visit times to the
  // nearest minute so dropping a card preserves the minute-by-minute preview.
  const tickMin = TICK_OPTIONS.find((m) => m * pxPerMin >= MIN_TICK_PX) || 60;

  // Drag a card's grip up/down to reschedule it. We move the card live, then
  // on release pin the snapped time (clamped to the day) via setVisitWindow.
  const startDrag = (event, ev) => {
    ev.stopPropagation(); // don't toggle recording / open the card
    ev.preventDefault();
    dragRef.current = {
      id: event.conversationId,
      startY: ev.clientY,
      origStartMs: event.startMs,
      durMs: event.endMs - event.startMs, // keep the duration when moving
    };
    setDragId(event.conversationId);
    setDragDelta(0);
    const startForY = (clientY, d) => {
      const deltaMin = (clientY - d.startY) / pxPerMin;
      const start = snapToMinute(d.origStartMs + deltaMin * MINUTE_MS);
      return Math.max(dayStartMs, Math.min(dayEndMs - d.durMs, start));
    };
    const move = (m) => {
      const d = dragRef.current;
      const delta = m.clientY - d.startY;
      if (Math.abs(delta) > 3) suppressCardClickRef.current = true;
      const start = startForY(m.clientY, d);
      setDragDelta(((start - d.origStartMs) / MINUTE_MS) * pxPerMin);
    };
    const up = (u) => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      const d = dragRef.current;
      dragRef.current = null;
      setDragId(null);
      setDragDelta(0);
      if (!d) return;
      if (Math.abs(u.clientY - d.startY) > 3) {
        suppressCardClickRef.current = true;
      }
      const start = startForY(u.clientY, d);
      if (start !== d.origStartMs) {
        actions.setVisitWindow(d.id, start, start + d.durMs);
      }
      window.setTimeout(() => {
        suppressCardClickRef.current = false;
      }, 100);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  // Click-and-drag an empty span to draw a new visit's start..end (Google-Cal
  // style). A bare click (no real drag) defaults to a 30-min visit.
  const yToMs = (clientY) => {
    const rect = gridRef.current?.getBoundingClientRect();
    if (!rect) return dayStartMs;
    return dayStartMs + ((clientY - rect.top) / pxPerMin) * 60000;
  };
  const startCreate = (ev) => {
    if (ev.target !== ev.currentTarget) return; // empty grid only
    ev.preventDefault();
    const startMs = yToMs(ev.clientY);
    setCreateDrag({ startMs, curMs: startMs });
    const move = (m) =>
      setCreateDrag((c) => (c ? { ...c, curMs: yToMs(m.clientY) } : c));
    const up = (u) => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      const endRaw = yToMs(u.clientY);
      let a = snapToGrid(Math.min(startMs, endRaw), tickMin);
      let b = snapToGrid(Math.max(startMs, endRaw), tickMin);
      if (b - a < MINUTE_MS) b = a + 30 * MINUTE_MS; // basically a click -> 30 min
      a = Math.max(dayStartMs, a);
      b = Math.min(dayEndMs, b);
      setCreateDrag(null);
      setAddWin({ startMs: a, endMs: b });
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  // Gridline ticks stay zoom-dependent for readability. Label hours always,
  // sub-hour ticks only when there's room.
  const labelMinor = tickMin < 60 && tickMin * pxPerMin >= 40;
  const ticks = [];
  for (let m = 0; m < 24 * 60; m += tickMin) {
    const onHour = m % 60 === 0;
    ticks.push({
      top: m * pxPerMin,
      major: onHour,
      // Full time-of-day on every label (e.g. "9:25a"), not just the hour.
      label: onHour || labelMinor ? fmtTime(dayStartMs + m * 60000) : null,
    });
  }

  // No caps — step to any day, past or future. (Days outside the loaded
  // window just render empty; nothing to gate.)
  const stepBtn = (dir) => (
    <i
      className={`fa fa-chevron-${dir === -1 ? 'left' : 'right'}`}
      aria-hidden="true"
      role="button"
      title={dir === -1 ? 'Previous day' : 'Next day'}
      onClick={() => setDayOffset((o) => o + dir)}
      style={{
        color: T.textSecondary,
        fontSize: 12,
        cursor: 'pointer',
        padding: '4px 8px',
      }}
    />
  );

  return (
    <div style={{ position: 'relative' }}>
      {/* Day stepper — pinned above the scrolling day. */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 4,
          padding: '6px 0 8px',
          background: T.surface,
        }}
      >
        {stepBtn(-1)}
        <span
          role={isToday ? undefined : 'button'}
          onClick={() => !isToday && setDayOffset(0)}
          title={isToday ? undefined : 'Jump to today'}
          style={{
            minWidth: 96,
            textAlign: 'center',
            fontSize: 12,
            fontWeight: 700,
            color: T.textPrimary,
            letterSpacing: '.3px',
            cursor: isToday ? 'default' : 'pointer',
          }}
        >
          {dayLabel(dayOffset)}
        </span>
        {stepBtn(1)}

        {/* Zoom — adjust the vertical scale (px per minute). */}
        <div
          style={{
            position: 'absolute',
            right: 4,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          {[
            {
              icon: 'fa-search-minus',
              factor: 1 / ZOOM_STEP,
              title: 'Zoom out',
            },
            { icon: 'fa-search-plus', factor: ZOOM_STEP, title: 'Zoom in' },
          ].map((z) => (
            <i
              key={z.icon}
              className={`fa ${z.icon}`}
              aria-hidden="true"
              role="button"
              title={z.title}
              onClick={() => zoom(z.factor)}
              style={{
                color: T.textFaint,
                fontSize: 12,
                cursor: 'pointer',
                padding: 4,
              }}
            />
          ))}
        </div>
      </div>

      {/* Drag an empty span to draw a new visit's start..end (a bare click =
          30 min). The `copy` cursor over empty time is the quiet affordance;
          visit cards are children, so e.target differs and they're skipped. */}
      <div
        ref={gridRef}
        onPointerDown={startCreate}
        title="Drag an empty span to add a visit"
        style={{ position: 'relative', height: dayHeight, cursor: 'copy' }}
      >
        {/* Time grid: hairlines at the zoom-appropriate interval (denser when
            zoomed in). Hour lines are stronger; sub-hour ticks are fainter. */}
        {ticks.map((tick) => (
          <React.Fragment key={tick.top}>
            <div
              style={{
                position: 'absolute',
                top: tick.top,
                left: GUTTER,
                right: 0,
                height: 1,
                background: T.border,
                opacity: tick.major ? 0.35 : 0.16,
                zIndex: 0,
                pointerEvents: 'none',
              }}
            />
            {tick.label && (
              <span
                style={{
                  position: 'absolute',
                  top: tick.top - 5,
                  left: 0,
                  width: GUTTER - 8,
                  textAlign: 'right',
                  fontSize: tick.major ? 10 : 9,
                  fontWeight: tick.major ? 600 : 400,
                  color: T.textFaint,
                  opacity: tick.major ? 1 : 0.7,
                  whiteSpace: 'nowrap',
                }}
              >
                {tick.label}
              </span>
            )}
          </React.Fragment>
        ))}

        {/* Auto-scroll anchor (now on today, first visit otherwise). */}
        <div
          ref={scrollTargetRef}
          style={{
            position: 'absolute',
            top: scrollTargetTop,
            height: 1,
            width: 1,
            pointerEvents: 'none',
          }}
        />

        {/* Red "now" line — only on today, at the real clock position. */}
        {isToday && nowMs >= dayStartMs && nowMs < dayEndMs && (
          <div
            style={{
              position: 'absolute',
              top: nowTop,
              left: GUTTER,
              right: 0,
              height: 2,
              background: T.red,
              zIndex: 0,
              pointerEvents: 'none',
            }}
          >
            <span
              style={{
                position: 'absolute',
                left: -5,
                top: -4,
                width: 10,
                height: 10,
                borderRadius: '50%',
                background: T.red,
              }}
            />
          </div>
        )}

        {/* Live ghost while drawing a new visit's span. */}
        {createDrag &&
          (() => {
            // Snap the ghost to ticks as you drag, so what you see is what
            // you'll get.
            const a = snapToMinute(
              Math.min(createDrag.startMs, createDrag.curMs),
            );
            const b = snapToMinute(
              Math.max(createDrag.startMs, createDrag.curMs),
            );
            const top = ((a - dayStartMs) / 60000) * pxPerMin;
            const h = Math.max(3, ((b - a) / 60000) * pxPerMin);
            return (
              <div
                style={{
                  position: 'absolute',
                  top,
                  left: GUTTER,
                  right: 4,
                  height: h,
                  background: 'rgba(45,206,137,0.18)',
                  border: '1px dashed rgba(45,206,137,0.7)',
                  borderRadius: 8,
                  zIndex: 4,
                  pointerEvents: 'none',
                  display: 'flex',
                  alignItems: 'flex-start',
                  padding: '3px 6px',
                  fontSize: 10,
                  fontWeight: 700,
                  color: T.greenBright,
                }}
              >
                {fmtTime(a)} – {fmtTime(b)}
              </div>
            );
          })()}

        {/* Visit blocks */}
        {laid.map((e) => {
          const session = sessionFor(e.conversationId);
          // Lit red whenever it's actually in the active recording set — not
          // gated to today, or a visit you tap to record while viewing
          // Yesterday never turns red. (activeSet is "now", so auto-window
          // visits only light on today anyway; a manual tap lights any day.)
          const recording =
            state.recordingDay && activeSet.has(e.conversationId);
          const accent = recording
            ? T.red
            : COLUMN_ACCENTS[e.col % COLUMN_ACCENTS.length];
          const convName = session?.name || e.patientName;
          // Status dot — same lifecycle scheme as the Conversations list
          // (ConversationRowName): red = no audio yet, yellow = recorded &
          // transcribed (needs Comprehend), transparent = done/in-progress.
          // Recording right now overrides with a pulsing red.
          const secs = session?.secondsTranscribed;
          const transcribed =
            secs > 0 &&
            typeof session?.status === 'string' &&
            session.status.startsWith('Transcription Complete!');
          const dotColor = recording
            ? T.red
            : !(secs > 0)
              ? 'rgb(242,105,105)'
              : transcribed
                ? 'rgb(255,208,0)'
                : 'transparent';
          const dotTitle = recording
            ? 'Recording now'
            : !(secs > 0)
              ? 'Not recorded yet'
              : transcribed
                ? "Recorded — don't forget to Comprehend"
                : '';
          const left = `calc(${GUTTER}px + ${e.col} * (100% - ${GUTTER}px) / ${e.cols})`;
          const width = `calc((100% - ${GUTTER}px) / ${e.cols} - 4px)`;
          const dragging = dragId === e.conversationId;
          const top = e.top + (dragging ? dragDelta : 0);
          const displayTime = fmtTime(
            e.startMs + (dragging ? (dragDelta / pxPerMin) * 60000 : 0),
          );
          return (
            <div
              key={e.conversationId}
              onClick={(ev) => {
                if (suppressCardClickRef.current) {
                  ev.stopPropagation();
                  suppressCardClickRef.current = false;
                  return;
                }
                actions.setVisitRecording(e.conversationId, !recording);
              }}
              onContextMenu={(ev) => {
                ev.preventDefault();
                setMenu({
                  x: ev.clientX,
                  y: ev.clientY,
                  conversationId: e.conversationId,
                  name: session?.name || e.patientName,
                  patientName: e.patientName,
                });
              }}
              role="button"
              title="Tap to start/stop recording · right-click for more"
              style={{
                position: 'absolute',
                top,
                height: e.height - 2,
                left,
                width,
                background: recording ? 'rgba(245,54,92,0.14)' : T.surfaceCard,
                border: `1px solid ${recording ? 'rgba(245,54,92,0.5)' : T.border}`,
                borderLeft: `3px solid ${accent}`,
                borderRadius: 8,
                padding: '3px 8px',
                cursor: 'pointer',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                gap: 0,
                zIndex: dragging ? 5 : 1,
                boxShadow: dragging ? '0 6px 18px rgba(0,0,0,0.45)' : 'none',
                opacity: dragging ? 0.95 : 1,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <span
                  className={recording ? 'cpl-dot' : undefined}
                  title={dotTitle}
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    flexShrink: 0,
                    background: dotColor,
                  }}
                />
                <span
                  style={{
                    flex: 1,
                    minWidth: 0,
                    fontSize: 12,
                    fontWeight: 600,
                    lineHeight: 1.2,
                    color: T.textPrimary,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {convName}
                </span>
                {e.height < SUBTITLE_MIN_H && (
                  <span
                    style={{
                      color: T.textFaint,
                      fontSize: 10,
                      flexShrink: 0,
                    }}
                  >
                    {displayTime}
                  </span>
                )}
                <i
                  className="fa fa-arrows-alt-v"
                  aria-hidden="true"
                  onClick={(ev) => {
                    ev.stopPropagation();
                    suppressCardClickRef.current = false;
                  }}
                  onPointerDown={(ev) => startDrag(e, ev)}
                  title="Drag to change the visit time"
                  style={{
                    color: dragging ? T.greenBright : T.textFaint,
                    fontSize: 11,
                    flexShrink: 0,
                    cursor: 'ns-resize',
                    touchAction: 'none',
                    padding: '0 2px',
                  }}
                />
                <i
                  className="fa fa-chevron-right"
                  aria-hidden="true"
                  onClick={(ev) => {
                    ev.stopPropagation();
                    actions.openVisit(e.conversationId);
                  }}
                  title="Open visit details"
                  style={{ color: T.textFaint, fontSize: 11, flexShrink: 0 }}
                />
              </div>
              {e.height >= SUBTITLE_MIN_H && (
                <span
                  style={{
                    fontSize: 10,
                    lineHeight: 1.2,
                    color: T.textFaint,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {e.patientName} · {displayTime}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {addWin && (
        <QuickAddVisit
          startMs={addWin.startMs}
          endMs={addWin.endMs}
          onClose={() => setAddWin(null)}
        />
      )}

      {menu && (
        <ContextMenu
          x={menu.x}
          y={menu.y}
          onView={() => actions.openVisit(menu.conversationId)}
          onRename={() => {
            const name = window.prompt('Rename visit', menu.name || '');
            if (name && name.trim()) {
              actions.renameVisit(menu.conversationId, name.trim());
            }
          }}
          onPatient={() => {
            const name = window.prompt(
              'Edit patient name',
              menu.patientName || '',
            );
            if (name && name.trim()) {
              actions.editPatientName(menu.conversationId, name.trim());
            }
          }}
          onClose={() => setMenu(null)}
        />
      )}
    </div>
  );
};

export default VerticalTimeline;
